import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1';

// In-memory token — never stored in a cookie or localStorage
let _csrfToken = null;
let _fetchPromise = null;

// Separate axios instance for CSRF to avoid circular dependency with base.js
const csrfAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// Fetch a fresh token from the server, deduplicated so only one request fires at a time
export const fetchCSRFToken = async () => {
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = csrfAxios
    .get('/security/csrf-token')
    .then((response) => {
      _csrfToken = response.data.token;
      if (!_csrfToken) throw new Error('CSRF token missing in server response');
      return _csrfToken;
    })
    .finally(() => {
      _fetchPromise = null;
    });

  return _fetchPromise;
};

// Return cached token synchronously (null if not yet fetched)
export const getCachedCSRFToken = () => _csrfToken;

// Invalidate cached token — call before retrying after a 403
export const clearCSRFToken = () => {
  _csrfToken = null;
};

// Setup CSRF interceptors on any axios instance
export const setupCSRF = (axiosInstance) => {
  // Request interceptor — attach in-memory token to mutating requests
  axiosInstance.interceptors.request.use(
    async (config) => {
      if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
        const token = _csrfToken || await fetchCSRFToken();
        if (token) {
          config.headers['X-XSRF-TOKEN'] = token;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor — on 403 CSRF error, clear token and retry once
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 403 &&
        error.response?.data?.message?.includes('CSRF') &&
        !originalRequest._csrfRetry
      ) {
        originalRequest._csrfRetry = true;
        try {
          clearCSRFToken();
          const freshToken = await fetchCSRFToken();
          if (freshToken) {
            originalRequest.headers['X-XSRF-TOKEN'] = freshToken;
          }
          return axiosInstance(originalRequest);
        } catch (csrfError) {
          console.error('Failed to refresh CSRF token:', csrfError);
        }
      }

      return Promise.reject(error);
    }
  );
};